<template>
  <b-modal
    id="receiver-risk-evidence-modal"
    size="xl"
    dialog-class="rr-modal-dialog"
    title="Receiver Linked Risk"
    hide-footer
    scrollable
    @show="onShow"
    @hidden="onHidden"
  >
    <template #modal-title>
      <div class="d-flex align-items-center">
        <h5 class="text-primary font-weight-normal mb-0 mr-3">
          Receiver Linked Risk — Evidence
        </h5>
        <span
          v-if="receiver && receiver.name"
          class="badge badge-light text-dark font-weight-normal"
        >
          {{ receiver.name }}
        </span>
      </div>
    </template>

    <div v-if="loading" class="text-center py-5">
      <i class="fa fa-spinner fa-spin fa-2x text-muted"></i>
      <p class="text-muted mt-2 mb-0">Computing linked risk…</p>
    </div>

    <div v-else-if="error" class="text-center py-5">
      <i class="fa fa-exclamation-triangle fa-2x text-warning"></i>
      <p class="text-muted mt-2 mb-0">{{ error }}</p>
    </div>

    <div v-else-if="evidence" class="rr-evidence">
      <div class="d-flex flex-wrap mb-4">
        <span class="badge rr-chip mr-2 mb-1" :class="badgeClass(evidence.level)">
          {{ evidence.level }} link risk
        </span>
        <span class="badge rr-chip badge-light border mr-2 mb-1">
          Score {{ evidence.score }}/100
        </span>
        <span class="badge rr-chip badge-light border mr-2 mb-1">
          {{ evidence.linkedRemitters }} linked remitter(s)
        </span>
        <span class="badge rr-chip badge-light border mb-1">
          {{ evidence.customerMatch }} shared identit{{ evidence.customerMatch === 1 ? "y" : "ies" }}
        </span>
      </div>

      <!-- Score breakdown -->
      <h6 class="rr-section-title">How the score was calculated</h6>
      <table class="table table-sm rr-score-table mb-4">
        <thead>
          <tr>
            <th class="rr-c1">Risk Factor</th>
            <th class="rr-c2">Evidence</th>
            <th class="rr-c3">Weight</th>
            <th class="rr-c4">Explanation</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(f, i) in evidence.factors" :key="i">
            <td class="font-weight-bold">{{ f.label }}</td>
            <td>{{ f.evidence }}</td>
            <td><span class="badge" :class="weightClass(f.weight)">{{ f.weight }}</span></td>
            <td class="text-muted">{{ f.explanation }}</td>
          </tr>
        </tbody>
      </table>

      <!-- Shared identities breakdown -->
      <h6 id="rr-sec-identities" class="rr-section-title">Shared payout details</h6>
      <p class="rr-help">
        Below are this receiver's payout numbers (Mobile Money or bank account) that also
        appear on <strong>another record</strong>. What matters is <em>who</em> the other
        record belongs to: a <strong>verified customer with the same name</strong> is almost
        always the same real person (self-registration or a duplicate — not a concern),
        while a <strong>different name</strong>, especially on a verified customer, can signal
        an alias. Unverified records are auto-created placeholders whose names are for
        reference only (they may be a nickname), so a name difference there is weak evidence.
      </p>
      <div v-if="evidence.sharedIdentityGroups && evidence.sharedIdentityGroups.length" class="mb-4">
        <div
          v-for="(g, i) in evidence.sharedIdentityGroups"
          :key="i"
          class="rr-group"
        >
          <div class="d-flex justify-content-between align-items-center flex-wrap mb-2">
            <span>
              <span class="badge mr-2" :class="g.type === 'bank' ? 'badge-danger' : 'badge-warning'">
                {{ g.label }}
              </span>
              <span class="rr-mono">{{ g.value }}</span>
            </span>
            <span class="badge badge-dark">
              on {{ g.identityCount }} other record{{ g.identityCount === 1 ? "" : "s" }}
            </span>
          </div>
          <div class="small text-muted mb-1">
            This {{ g.type === "bank" ? "bank account" : "Mobile Money number" }} also appears
            on {{ g.identityCount === 1 ? "1 other record" : g.identityCount + " other records" }}:
          </div>
          <div class="rr-identities">
            <div v-for="(idn, j) in g.sampleIdentities" :key="j" class="rr-identity-row">
              <span class="rr-identity-name">{{ idn.name }}</span>
              <span class="rr-identity-kind" :class="idn.kind === 'verified' ? 'rr-kind-verified' : 'rr-kind-prospective'">
                {{ idn.kind === "verified" ? "verified customer" : "unverified record" }}
              </span>
              <span class="rr-identity-note" :class="identityToneClass(idn)">
                {{ identityNote(idn) }}
              </span>
            </div>
          </div>
        </div>
      </div>
      <p v-else class="text-muted small mb-4">
        None of this receiver's Mobile Money numbers or bank accounts appear on any other
        record — no shared-identity concern.
      </p>

      <!-- Senders (who sends to this receiver) -->
      <h6 id="rr-sec-remitters" class="rr-section-title">Remitters who send to this receiver</h6>
      <p class="rr-help">
        Registered customers who added this receiver as their beneficiary and send money to them.
      </p>
      <div v-if="directSenders.length" class="rr-group">
        <div
          class="d-flex align-items-center justify-content-between flex-wrap mb-2"
          style="gap: 8px"
        >
          <span class="badge badge-dark">
            {{ directSenders.length }} remitter{{ directSenders.length === 1 ? "" : "s" }}
          </span>
          <input
            v-if="directSenders.length > 8"
            v-model="senderSearch"
            type="text"
            class="form-control form-control-sm rr-search"
            placeholder="Search name, PayAngel ID or phone…"
          />
        </div>
        <div class="rr-remitter-scroll">
          <table class="table table-sm mb-0 rr-remitter-table">
            <thead>
              <tr>
                <th class="rr-r1">Remitter</th>
                <th class="rr-r2">PayAngel ID</th>
                <th class="rr-r3">Phone</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(r, k) in filteredSenders" :key="k">
                <td>{{ r.name }}</td>
                <td>{{ r.payangelId || "—" }}</td>
                <td>{{ r.phone || "—" }}</td>
              </tr>
              <tr v-if="!filteredSenders.length">
                <td colspan="3" class="text-muted text-center py-2">
                  No remitters match “{{ senderSearch }}”.
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-if="senderSearch && filteredSenders.length" class="rr-more">
          Showing {{ filteredSenders.length }} of {{ directSenders.length }} remitters.
        </div>
      </div>
      <p v-else class="text-muted small">No genuine remitters send to this receiver.</p>

      <p class="text-muted small mt-3 mb-0">
        Receiver data comes from the beneficiary record. Only genuine remitters are
        counted. This score is informational — Monitor, Restrict, or Blacklist still
        requires an analyst reason.
      </p>
    </div>
  </b-modal>
</template>

<script>
import { GET_RECEIVER_RISK_EVIDENCE } from "@/store/actions.type.js";

export default {
  name: "ReceiverRiskEvidenceModal",
  props: {
    receiver: { type: Object, default: null },
  },
  data() {
    return { loading: false, error: null, evidence: null, senderSearch: "" };
  },
  computed: {
    // The direct-senders list (backend returns a single 'exact' entry for receivers).
    directSenders() {
      const inst = this.evidence?.links?.[0]?.instruments?.[0];
      return inst ? inst.sampleRemitters : [];
    },
    // Client-side search across the full remitter list (name / PayAngel ID / phone).
    filteredSenders() {
      const q = (this.senderSearch || "").trim().toLowerCase();
      if (!q) return this.directSenders;
      return this.directSenders.filter((r) =>
        [r.name, r.payangelId, r.phone].some((v) => (v || "").toLowerCase().includes(q))
      );
    },
    linkedRemittersTotal() {
      return this.evidence?.links?.[0]?.instruments?.[0]?.remitterCount ?? 0;
    },
  },
  methods: {
    onShow() {
      this.$nextTick(() => this.load());
    },
    async load() {
      this.evidence = null;
      this.error = null;
      this.senderSearch = "";
      if (!this.receiver || !this.receiver.beneficiaryid) {
        this.error = "No receiver selected.";
        return;
      }
      this.loading = true;
      const res = await this.$store.dispatch(
        GET_RECEIVER_RISK_EVIDENCE,
        this.receiver.beneficiaryid
      );
      this.loading = false;
      if (res && res.status == 200 && res.data.details) {
        this.evidence = res.data.details;
        // Land the analyst on the section they clicked (Linked Remitters vs Shared Identities).
        this.$nextTick(() => this.scrollToFocus());
      } else {
        this.error = "Unable to load the risk breakdown for this receiver.";
      }
    },
    scrollToFocus() {
      const map = { identities: "rr-sec-identities", remitters: "rr-sec-remitters" };
      const id = map[this.receiver && this.receiver.focus];
      if (!id) return;
      const el = document.getElementById(id);
      if (el && el.scrollIntoView) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    },
    onHidden() {
      this.evidence = null;
      this.error = null;
      this.loading = false;
      this.senderSearch = "";
    },
    // Plain-language read of one shared identity, using the backend classification
    // (verified vs unverified customer, same vs different name).
    identityNote(idn) {
      if (idn.kind === "verified") {
        return idn.sameName
          ? "same person — likely a self-registration (not a concern)"
          : "different name on a verified customer — possible alias";
      }
      return idn.sameName
        ? "same name — likely a duplicate beneficiary entry"
        : "different name (reference only — may be a nickname)";
    },
    // Tone: only a verified customer under a different name is a real red flag.
    identityToneClass(idn) {
      if (idn.kind === "verified" && !idn.sameName) return "rr-tone-alias";
      if (idn.sameName) return "rr-tone-benign";
      return "rr-tone-soft";
    },
    badgeClass(level) {
      if (level === "High") return "badge-danger";
      if (level === "Medium") return "badge-warning";
      return "badge-success";
    },
    weightClass(weight) {
      if (weight === "High") return "badge-danger";
      if (weight === "Medium") return "badge-warning";
      if (weight === "Low") return "badge-success";
      if (weight && weight.indexOf("+") === 0 && weight !== "+0") return "badge-info";
      return "badge-light border";
    },
  },
};
</script>

<style scoped>
.rr-evidence {
  font-size: 14px;
}
.rr-section-title {
  text-transform: uppercase;
  letter-spacing: 0.04em;
  font-size: 12px;
  font-weight: 700;
  color: #6b7782;
  margin-bottom: 10px;
}
.rr-chip {
  font-size: 12px;
  padding: 6px 10px;
}
.rr-help {
  font-size: 13px;
  color: #56606a;
  margin-bottom: 10px;
}
.rr-search {
  max-width: 300px;
}
.rr-score-table {
  table-layout: fixed;
  width: 100%;
}
.rr-score-table th,
.rr-score-table td {
  vertical-align: top;
  word-break: break-word;
  white-space: normal;
}
.rr-c1 { width: 18%; }
.rr-c2 { width: 34%; }
.rr-c3 { width: 10%; }
.rr-c4 { width: 38%; }
.rr-group {
  border: 1px solid #e3e9ee;
  border-radius: 6px;
  padding: 12px 14px;
  margin-bottom: 12px;
  background: #fbfcfd;
}
.rr-mono {
  font-family: monospace;
  word-break: break-all;
}
.rr-names {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.rr-name-chip {
  background: #eef2f5;
  border-radius: 4px;
  padding: 2px 8px;
  font-size: 12px;
}
.rr-identities {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.rr-identity-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}
.rr-identity-name {
  font-weight: 600;
  font-size: 13px;
}
.rr-identity-kind {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  padding: 1px 6px;
  border-radius: 3px;
}
.rr-kind-verified {
  background: #e3f0e6;
  color: #256a37;
}
.rr-kind-prospective {
  background: #eceff1;
  color: #5b6770;
}
.rr-identity-note {
  font-size: 12px;
}
.rr-tone-alias {
  color: #a72727;
  font-weight: 600;
}
.rr-tone-benign {
  color: #256a37;
}
.rr-tone-soft {
  color: #8d6100;
}
.rr-remitter-scroll {
  max-height: 260px;
  overflow-y: auto;
  border: 1px solid #edf1f4;
  border-radius: 4px;
}
.rr-remitter-table {
  table-layout: fixed;
  width: 100%;
}
.rr-remitter-table th,
.rr-remitter-table td {
  word-break: break-word;
}
.rr-remitter-table thead th {
  position: sticky;
  top: 0;
  background: #f4f7f9;
  border-top: 0;
}
.rr-r1 { width: 45%; }
.rr-r2 { width: 25%; }
.rr-r3 { width: 30%; }
.rr-more {
  font-size: 12px;
  color: #6b7782;
  font-style: italic;
  margin-top: 4px;
}
</style>

<!-- Not scoped: BootstrapVue teleports .modal-dialog to <body>. -->
<style>
.rr-modal-dialog {
  max-width: 1280px;
  width: 94vw;
}
</style>
